import * as React from 'react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { StocktakeLine } from '@/hooks/useStocktakes'

// Show a short list — the operator is standing at a rack, not browsing.
const MAX_RESULTS = 8

/** Typeahead over THIS stocktake's lines, sitting next to the scan bar so a tag
 *  that won't scan can be counted without leaving the Scan tab. Picking a result
 *  records +1, exactly like a scan would.
 *
 *  Deliberately NOT ProductSearchPicker: that one searches the whole catalogue,
 *  and adding a variant that isn't part of this stocktake is a 404. Filtering is
 *  client-side over lines we already hold, so there's nothing to debounce. */
export function StocktakeItemSearch({
  lines,
  onPick,
  disabled,
}: {
  lines: StocktakeLine[]
  onPick: (line: StocktakeLine) => void
  disabled?: boolean
}) {
  const [query, setQuery] = React.useState('')
  const [active, setActive] = React.useState(0)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const term = query.trim().toLowerCase()
  const results = React.useMemo(() => {
    if (!term) return []
    return lines
      .filter((l) =>
        [l.product_title, l.variant_title, l.sku, l.barcode].some((f) =>
          (f ?? '').toLowerCase().includes(term)
        )
      )
      .slice(0, MAX_RESULTS)
  }, [lines, term])

  // A narrowing query can leave the cursor past the end of the shorter list.
  React.useEffect(() => {
    setActive(0)
  }, [term])

  function pick(line: StocktakeLine) {
    onPick(line)
    setQuery('')
    setActive(0)
    // Stay put: repeated Enter keeps adding without a trip to the mouse.
    inputRef.current?.focus()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      e.preventDefault()
      setQuery('')
      return
    }
    if (results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((i) => (i + 1) % results.length)
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((i) => (i - 1 + results.length) % results.length)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const line = results[active]
      if (line) pick(line)
    }
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-label="Product search"
        placeholder="Product search"
        className="h-8 text-xs"
        data-testid="stocktake-item-search"
      />
      {term && (
        <div
          className="absolute z-20 mt-1 max-h-[18rem] w-full overflow-y-auto border border-[var(--line-warm)] bg-[var(--surface-card)] shadow-md"
          data-testid="stocktake-item-search-results"
        >
          {results.length === 0 ? (
            <div
              className="px-2.5 py-2 text-xs text-[var(--mute)]"
              data-testid="stocktake-item-search-empty"
            >
              No matching items in this stocktake.
            </div>
          ) : (
            results.map((l, i) => (
              <button
                key={l.line_id}
                type="button"
                // mousedown, not click: the input's blur must not tear the list
                // down before the click lands.
                onMouseDown={(e) => {
                  e.preventDefault()
                  pick(l)
                }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  'flex w-full items-center justify-between gap-3 border-b border-[var(--line-warm)] px-2.5 py-1.5 text-left last:border-b-0',
                  i === active && 'bg-[var(--surface-header)]'
                )}
                data-testid={`stocktake-item-search-result-${l.line_id}`}
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-medium text-[var(--text-primary)]">
                    {l.product_title || '—'}
                    {l.variant_title ? ` · ${l.variant_title}` : ''}
                  </span>
                  <span className="block truncate font-mono text-[10px] text-[var(--mute)]">
                    {l.sku || '—'}
                  </span>
                </span>
                {/* null is NOT 0: "not counted" is what leaves Shopify alone. */}
                <span
                  className={cn(
                    'shrink-0 text-[11px] tabular-nums',
                    l.counted_qty == null
                      ? 'text-[var(--mute)]'
                      : 'font-semibold text-[var(--text-primary)]'
                  )}
                >
                  {l.counted_qty == null ? 'not counted' : `Scanned ${l.counted_qty}`}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
